import React from "react";
import AgentWorkflow from "../components/AgentWorkflow";
import { renderOfferBlueprint } from "../components/blueprintRenderers";
import { pickRandomOfferSample } from "../lib/randomOfferSamples";

const ACCENT = "#5e50f7";

const steps = [
  {
    key: "nagy_otlet",
    eyebrowLabel: "01 · A NAGY ÖTLET",
    question: "Nagy vonalakban mit szeretnél nyújtani?",
    placeholder:
      "pl. egy 8 hetes csoportos coaching program, ami webinar-funnel rendszert épít az ügyfeleknek",
    type: "textarea",
    rows: 3,
  },
  {
    key: "vegeredmeny",
    eyebrowLabel: "02 · A VÉGEREDMÉNY",
    question:
      "Milyen konkrét eredményt érjen el az ideális ügyfeled a végére? Mit lásson, érezzen?",
    placeholder:
      "pl. heti 5-10 minősített discovery hívás a naptárban, működő webinar funnel, kiszámítható havi 3M Ft bevétel",
    type: "textarea",
    rows: 3,
  },
  {
    key: "lepesek",
    eyebrowLabel: "03 · A LÉPÉSEK",
    question:
      "Hány lépésből szeretnéd, hogy ezt elérje? Milyen hosszú legyen a program?",
    placeholder:
      "pl. 8 hét, 4 fázis · vagy: 30 napos mini-kurzus · vagy: hagyd üresen, eldöntöm érted",
    type: "textarea",
    rows: 2,
  },
  {
    key: "ajanlat_forma",
    eyebrowLabel: "04 · AJÁNLAT FORMA",
    question: "Hogyan szeretnél vele együtt dolgozni?",
    placeholder:
      "pl. high ticket program, 12 modul, 4 egyéni coaching, heti csoportos coachingok, sablonok",
    type: "textarea",
    rows: 3,
  },
];

/**
 * Serialize both Express (fast) and Építő (guided) modes into the backend
 * OfferInput shape. Mode is carried via contentState.mode.
 */
function serializeValues(values, content) {
  const avatarSourceId = content?.avatarSourceId || null;
  const mode = content?.mode || "guided";

  if (mode === "fast") {
    const paste = (content?.writingSample || content?.paste || "").trim();
    const urls = Array.isArray(content?.urls) ? content.urls.filter(Boolean) : [];
    const lines = [];
    if (paste) lines.push(paste);
    if (urls.length > 0) {
      lines.push("", "Forrás URL-ek:");
      lines.push(...urls.map((u) => `- ${u}`));
    }
    return {
      avatar_source_id: avatarSourceId,
      user_context: lines.join("\n"),
      nagy_otlet: "",
      vegeredmeny: "",
      lepesek: "",
      ajanlat_forma: "",
    };
  }

  return {
    avatar_source_id: avatarSourceId,
    nagy_otlet: (values.nagy_otlet || "").trim(),
    vegeredmeny: (values.vegeredmeny || "").trim(),
    lepesek: (values.lepesek || "").trim(),
    ajanlat_forma: (values.ajanlat_forma || "").trim(),
    user_context: null,
  };
}

/**
 * Reverse map for hydration when localStorage draft is empty. Detection:
 * Express payload has `user_context` populated and all four guided fields
 * empty. Restores `mode: "fast"` and `writingSample`; leaves urls empty.
 * `avatar_source_id` is separately propagated back from the live doc via
 * downstream sidebar Aktív alap logic.
 */
function deserializeValues(inputs) {
  if (!inputs || typeof inputs !== "object") return null;
  const uc = (inputs.user_context || "").trim();
  const guidedFilled = [
    inputs.nagy_otlet, inputs.vegeredmeny, inputs.lepesek, inputs.ajanlat_forma,
  ].some((v) => (v || "").trim().length > 0);
  if (uc && !guidedFilled) {
    return { mode: "fast", writingSample: uc, paste: "", urls: [] };
  }
  return null;
}

// Same 10 niches as the Avatar Varázsló — single DEMO button that
// permutes through them is wired via `pickRandomSample` (see
// /app/frontend/src/lib/randomOfferSamples.js).

export default function OfferCraft() {
  return (
    <AgentWorkflow
      accent={ACCENT}
      agentKey="offer"
      dashboardKey="offer"
      blueprintName="Ajánlat"
      saveEndpoint="/foundation/offer/save"
      eyebrow="ALAPOZÁS · 03 · AJÁNLAT"
      title="Ajánlat Varázsló"
      agentName="Ajánlat Varázsló"
      deliverableLabel="Ajánlat"
      readyTitle="Elkészült a Prémium Ajánlatod."
      subtitle="Avatar + 4 kérdés (vagy egy meglévő ajánlat). Egy komplett Grand Slam ajánlat-csomag magyar piacra."
      greeting="Szia! Az elmentett avatarodra és hangodra építve segítek úgy összerakni az ajánlatodat, hogy az ne csak logikus legyen, hanem vágyat, bizalmat és azonnali döntést építsen."
      fastLabel="Ajánlat Expressz"
      fastDesc="Meglévő ajánlatod prémium szintű javítása"
      guidedLabel="Ajánlat Építő"
      guidedDesc="Új, erős ajánlatstruktúra felépítése kérdésekből"
      modePrompt=""
      guidedIntro={
        <>
          Kérjük válaszolj az alábbi kérdésekre, és segítünk egy »ellenállhatatlan ajánlatot«
          <br />
          összeállítani a célközönséged számára.
        </>
      }
      steps={steps}
      eyebrowVariant="brand"
      showContentInput={false}
      fastVariant="triad"
      requiresAvatarSource={true}
      serializeValues={serializeValues}
      deserializeValues={deserializeValues}
      generateEndpoint="/foundation/offer/generate"
      loadEndpoint="/foundation/offer"
      refineEndpoint="/foundation/offer/refine"
      demoFillEndpoint="/foundation/offer/demo-fill"
      renderBlueprint={renderOfferBlueprint}
      pickRandomSample={pickRandomOfferSample}
    />
  );
}
